// «Агент → Канал» (этап 6, PLAN §6): автопостинг в Telegram-канал. Посты делает агент — каждое утро плановой
// работой «Пост в канал» заполняет свободные слоты на сегодня и завтра (по умолчанию 10:00, 15:00, 20:00; время
// слотов меняет владелец здесь же), заявки channel.post идут через шлюз; владелец подключает канал, одобряет посты,
// пишет свои, снимает лишние. Видео в канале — с водяным знаком логотипа (накладывает платформа). Подключить канал может только владелец: бот должен быть администратором с правом публиковать.
// У вышедшего поста — просмотры (витрина t.me открытого канала), визиты и лиды по метке его ссылок (tg_<id>).
import { Button, Card, Chip, dateShort, EmptyState, IconButton, Skeleton, toast, when } from '@autoaff/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  Check,
  ExternalLink,
  Film,
  Image as ImageIcon,
  Images,
  Megaphone,
  Plus,
  Send,
  X,
} from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { api } from '../../api';
import { useIntegrations } from '../../business';

interface ChannelInfo {
  id: number;
  title: string | null;
  username: string | null;
}

interface Post {
  id: number;
  status: 'draft' | 'pending' | 'scheduled' | 'posting' | 'posted' | 'failed' | 'cancelled';
  text: string;
  media: Array<{ kind: 'photo' | 'video'; title: string | null }>;
  mediaType: 'text' | 'photo' | 'video' | 'album';
  button: { text: string; url: string } | null;
  offer: string | null;
  scheduledAt: string | null;
  postedAt: string | null;
  messageId: number | null;
  views: number | null;
  stats: { visits: number; leads: number; approved: number } | null;
  createdBy: 'agent' | 'owner';
  proposalId: string | null;
  error: string | null;
}

interface Slot {
  day: string;
  time: string;
  at: string;
  past: boolean;
  post: { id: number; status: Post['status']; offer: string | null } | null;
}

interface ChannelView {
  channel: ChannelInfo | null;
  candidates: Array<ChannelInfo & { at: string }>;
  lastError: string | null;
  schedule: { enabled: boolean; cron: string } | null;
  /** Время постов по Ташкенту — по посту в каждый слот. */
  slots: string[];
  /** Слоты на сегодня и завтра: какой занят каким постом. */
  slotPlan: Slot[];
  watermarkAssetId: number;
  streak: { days: number; posted: number };
  upcoming: Post[];
  history: Post[];
}

const STATUS: Record<
  Post['status'],
  { label: string; tone: 'default' | 'accent' | 'warning' | 'danger' | 'success' }
> = {
  draft: { label: 'Черновик', tone: 'default' },
  pending: { label: 'Ждёт вашего решения', tone: 'accent' },
  scheduled: { label: 'В расписании', tone: 'default' },
  posting: { label: 'Публикуется', tone: 'default' },
  posted: { label: 'Вышел', tone: 'success' },
  failed: { label: 'Не вышел', tone: 'danger' },
  cancelled: { label: 'Снят', tone: 'default' },
};

/** Текст поста без разметки Telegram — для предпросмотра (теги не исполняем). */
const plain = (html: string) =>
  html
    .replace(/<[^>]+>/g, '')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&');

/** Значение для <input type="datetime-local"> по Ташкенту (UTC+5) и обратно. */
const toLocal = (d: Date) => new Date(d.getTime() + 5 * 3_600_000).toISOString().slice(0, 16);
const fromLocal = (v: string) => `${v}:00+05:00`;

const num = (n: number) => n.toLocaleString('ru-RU');

/** Время ежедневной работы из cron «м ч * * *» — «08:00»; другой вид — null. */
const cronTime = (cron: string | undefined) => {
  const m = /^(\d{1,2}) (\d{1,2}) \* \* \*$/.exec(cron ?? '');
  return m ? `${(m[2] ?? '').padStart(2, '0')}:${(m[1] ?? '').padStart(2, '0')}` : null;
};

const SLOTS_MAX = 6;

/** Слоты постов: время, занятость на сегодня и завтра, правка времени. */
function SlotsCard({ d }: { d: ChannelView }) {
  const qc = useQueryClient();
  // Строки редактора — со своим номером: ключ не сдвигается, когда слот убирают из середины
  const [edit, setEdit] = useState<Array<{ key: number; time: string }> | null>(null);
  const start = () => setEdit(d.slots.map((time, key) => ({ key, time })));
  const [busy, setBusy] = useState(false);
  const runAt = cronTime(d.schedule?.cron);
  const save = async () => {
    if (!edit) return;
    setBusy(true);
    try {
      await api('/api/channel/slots', { slots: edit.map((x) => x.time).filter(Boolean) }, 'PUT');
      toast('Время постов сохранено — агент заполнит новые слоты в следующей плановой работе');
      setEdit(null);
      await qc.invalidateQueries({ queryKey: ['channel'] });
    } catch (e) {
      toast((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const days = [...new Set(d.slotPlan.map((x) => x.day))];
  return (
    <Card
      title="Слоты постов"
      action={
        !edit && (
          <Button size="sm" variant="ghost" onClick={start}>
            Изменить время
          </Button>
        )
      }
    >
      <p className="ch-slots-note">
        По посту в каждый слот, товары разные: <b className="tnum">{d.slots.join(' · ')}</b> по Ташкенту.{' '}
        <span className="muted">
          {d.schedule?.enabled
            ? `Агент заполняет свободные слоты на сегодня и завтра каждый день${runAt ? ` в ${runAt}` : ''}.`
            : 'Плановая работа «Пост в канал» выключена — слоты сами не заполнятся.'}{' '}
          Видео выходят с водяным знаком логотипа канала.
        </span>
      </p>
      {edit ? (
        <form
          className="ch-slots-edit"
          onSubmit={(e) => {
            e.preventDefault();
            void save();
          }}
        >
          {edit.map((x, i) => (
            <span key={x.key} className="ch-slot-input">
              <input
                type="time"
                className="input"
                value={x.time}
                aria-label={`Слот ${i + 1}, время по Ташкенту`}
                onChange={(e) =>
                  setEdit(edit.map((y) => (y.key === x.key ? { ...y, time: e.target.value } : y)))
                }
              />
              {edit.length > 1 && (
                <IconButton
                  label={`Убрать слот ${x.time || i + 1}`}
                  onClick={() => setEdit(edit.filter((y) => y.key !== x.key))}
                >
                  <X />
                </IconButton>
              )}
            </span>
          ))}
          {edit.length < SLOTS_MAX && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() =>
                setEdit([...edit, { key: Math.max(-1, ...edit.map((y) => y.key)) + 1, time: '12:00' }])
              }
            >
              <Plus />
              Добавить время
            </Button>
          )}
          <span className="ch-slots-acts">
            <Button type="submit" size="sm" variant="primary" disabled={busy || !edit.some((x) => x.time)}>
              Сохранить
            </Button>
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => setEdit(null)}>
              Отмена
            </Button>
          </span>
        </form>
      ) : (
        <div className="ch-slots">
          {days.map((day) => (
            <section key={day} aria-label={`Слоты: ${day}`}>
              <h4>{dateShort(`${day}T12:00:00+05:00`)}</h4>
              <ul>
                {d.slotPlan
                  .filter((x) => x.day === day)
                  .map((x) => (
                    <li key={x.at} data-state={x.post ? 'busy' : x.past ? 'past' : 'free'}>
                      <time className="tnum" dateTime={x.at}>
                        {x.time}
                      </time>
                      {x.post ? (
                        <span>
                          Пост <span className="tnum">#{x.post.id}</span> · {STATUS[x.post.status].label}
                          {x.post.offer && <span className="muted"> · {x.post.offer}</span>}
                        </span>
                      ) : (
                        <span className="muted">
                          {x.past ? 'прошёл без поста' : 'свободен — заполнит агент'}
                        </span>
                      )}
                    </li>
                  ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </Card>
  );
}

/** Медиа поста словами: «фото», «видео», «альбом · 4». */
function MediaTag({ p }: { p: Post }) {
  if (p.mediaType === 'text') return null;
  const first = p.media[0];
  const Icon = p.mediaType === 'album' ? Images : p.mediaType === 'photo' ? ImageIcon : Film;
  const label =
    p.mediaType === 'album'
      ? `альбом · ${p.media.length}`
      : (first?.title ?? (p.mediaType === 'photo' ? 'фото' : 'видео'));
  return (
    <span className="ch-media">
      <Icon aria-hidden />
      {label}
    </span>
  );
}

/** Ссылка кнопки без меток — коротко: shop.panda-manda.uz/market/tizzalik/. */
const shortUrl = (url: string) => {
  try {
    const u = new URL(url);
    return `${u.host}${u.pathname}`;
  } catch {
    return url;
  }
};

function ConnectCard({ d, bot }: { d: ChannelView; bot: string | null }) {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const connect = async (body: { chatId?: number; username?: string }) => {
    setBusy(true);
    try {
      await api('/api/channel/connect', body);
      toast('Канал подключён — агент будет публиковать посты каждый день по слотам');
      await qc.invalidateQueries({ queryKey: ['channel'] });
    } catch (e) {
      toast((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (name.trim()) void connect({ username: name.trim() });
  };
  return (
    <Card title="Подключите канал">
      <ol className="ch-steps">
        <li>
          Добавьте бота {bot ? <b>@{bot}</b> : 'платформы'} в администраторы канала с правом «Публикация
          сообщений».
        </li>
        <li>Выберите канал ниже или впишите его @имя.</li>
      </ol>
      {d.lastError && <p className="ch-error">{d.lastError}</p>}
      {d.candidates.length > 0 && (
        <ul className="ch-cands">
          {d.candidates.map((c) => (
            <li key={c.id}>
              <span>
                <b>{c.title ?? `Канал ${c.id}`}</b>
                {c.username && <span className="muted"> @{c.username}</span>}
                <span className="sub">бота сделали администратором {when(c.at)}</span>
              </span>
              <Button size="sm" variant="primary" disabled={busy} onClick={() => connect({ chatId: c.id })}>
                Подключить
              </Button>
            </li>
          ))}
        </ul>
      )}
      <form className="ch-connect" onSubmit={submit}>
        <input
          className="input"
          placeholder="@имя_канала"
          aria-label="Имя канала"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <Button type="submit" disabled={busy || !name.trim()}>
          Подключить
        </Button>
      </form>
    </Card>
  );
}

function PostCard({ p, channel }: { p: Post; channel: ChannelInfo | null }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const st = STATUS[p.status];
  const act = async (fn: () => Promise<unknown>, done: string) => {
    setBusy(true);
    try {
      await fn();
      toast(done);
      await qc.invalidateQueries({ queryKey: ['channel'] });
    } catch (e) {
      toast((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const link =
    p.status === 'posted' && channel?.username && p.messageId
      ? `https://t.me/${channel.username}/${p.messageId}`
      : null;
  const at = p.postedAt ?? p.scheduledAt;
  return (
    <article className="ch-post" data-status={p.status}>
      <header>
        <time dateTime={at ?? undefined}>{at ? when(at) : '—'}</time>
        <Chip tone={st.tone} dot={p.status === 'pending'}>
          {st.label}
        </Chip>
        <span className="muted">{p.createdBy === 'agent' ? 'агент' : 'вы'}</span>
        <MediaTag p={p} />
        {p.offer && <span className="muted">{p.offer}</span>}
      </header>
      <p className="ch-text">{plain(p.text)}</p>
      {p.button && (
        <a
          className="ch-btn"
          href={p.button.url}
          target="_blank"
          rel="noreferrer"
          title={shortUrl(p.button.url)}
        >
          {p.button.text}
          <ExternalLink aria-hidden />
        </a>
      )}
      {p.status === 'posted' && p.stats && (
        <p className="ch-stats tnum">
          <span>
            Просмотры <b>{p.views === null ? '—' : num(p.views)}</b>
          </span>
          <span>
            Визиты <b>{num(p.stats.visits)}</b>
          </span>
          <span>
            Лиды <b>{num(p.stats.leads)}</b>
          </span>
          <span>
            Апрувы <b>{num(p.stats.approved)}</b>
          </span>
        </p>
      )}
      {p.error && p.status !== 'posted' && <p className="ch-error">{p.error}</p>}
      <div className="ch-acts">
        {p.status === 'pending' && p.proposalId && (
          <>
            <Button
              size="sm"
              variant="primary"
              disabled={busy}
              onClick={() =>
                act(
                  () => api(`/api/agent/proposals/${p.proposalId}/decide`, { decision: 'approve' }),
                  'Пост в расписании',
                )
              }
            >
              <Check />
              Одобрить
            </Button>
            <Button
              size="sm"
              disabled={busy}
              onClick={() =>
                act(
                  () => api(`/api/agent/proposals/${p.proposalId}/decide`, { decision: 'reject' }),
                  'Пост снят',
                )
              }
            >
              <X />
              Отклонить
            </Button>
          </>
        )}
        {(p.status === 'scheduled' || p.status === 'failed') && (
          <Button
            size="sm"
            disabled={busy}
            onClick={() =>
              act(
                () => api(`/api/channel/posts/${p.id}`, { at: new Date().toISOString() }, 'PATCH'),
                'Пост выйдет в течение минуты',
              )
            }
          >
            <Send />
            {p.status === 'failed' ? 'Опубликовать снова' : 'Опубликовать сейчас'}
          </Button>
        )}
        {['draft', 'scheduled', 'failed'].includes(p.status) && (
          <Button
            size="sm"
            variant="ghost"
            disabled={busy}
            onClick={() => act(() => api(`/api/channel/posts/${p.id}/cancel`, {}), 'Пост снят')}
          >
            Снять
          </Button>
        )}
        {link && (
          <a className="btn btn-sm btn-ghost" href={link} target="_blank" rel="noreferrer">
            Открыть в канале
          </a>
        )}
      </div>
    </article>
  );
}

function NewPost() {
  const qc = useQueryClient();
  const [text, setText] = useState('');
  const [link, setLink] = useState('');
  const [at, setAt] = useState(() => toLocal(new Date(Date.now() + 3_600_000)));
  const [busy, setBusy] = useState(false);
  const send = async (now: boolean) => {
    setBusy(true);
    try {
      await api('/api/channel/posts', {
        text,
        ...(now ? {} : { at: fromLocal(at) }),
        ...(link.trim() ? { buttonUrl: link.trim() } : {}),
      });
      toast(now ? 'Пост выйдет в течение минуты' : 'Пост в расписании');
      setText('');
      setLink('');
      await qc.invalidateQueries({ queryKey: ['channel'] });
    } catch (e) {
      toast((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card title="Свой пост">
      <form
        className="ch-new"
        onSubmit={(e) => {
          e.preventDefault();
          void send(false);
        }}
      >
        <textarea
          className="input"
          rows={4}
          maxLength={4096}
          placeholder="Текст поста — как обычное сообщение в канале"
          aria-label="Текст поста"
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        <div className="ch-new-row">
          <label>
            <span className="muted">Кнопка «Buyurtma berish» — ссылка (необязательно)</span>
            <input
              className="input"
              type="url"
              inputMode="url"
              placeholder="https://shop.panda-manda.uz/market/…/"
              value={link}
              onChange={(e) => setLink(e.target.value)}
            />
          </label>
          <label>
            <span className="muted">Когда (Ташкент)</span>
            <input
              className="input"
              type="datetime-local"
              value={at}
              onChange={(e) => setAt(e.target.value)}
            />
          </label>
          <Button type="submit" variant="primary" disabled={busy || text.trim().length < 2}>
            Запланировать
          </Button>
          <Button type="button" disabled={busy || text.trim().length < 2} onClick={() => send(true)}>
            Опубликовать сейчас
          </Button>
        </div>
      </form>
    </Card>
  );
}

export function ChannelTab() {
  const q = useQuery({
    queryKey: ['channel'],
    queryFn: () => api<ChannelView>('/api/channel'),
    refetchInterval: 30_000,
  });
  const integ = useIntegrations();
  const bot = integ.data?.telegram.botUsername ?? null;
  const qc = useQueryClient();
  const d = q.data;
  const disconnect = async () => {
    try {
      await api('/api/channel/disconnect', {});
      toast('Канал отключён — посты не выходят, агент их не публикует');
      await qc.invalidateQueries({ queryKey: ['channel'] });
    } catch (e) {
      toast((e as Error).message);
    }
  };

  if (q.isPending)
    return (
      <div className="pc">
        <Skeleton h={120} />
      </div>
    );
  if (!d)
    return (
      <div className="pc">
        <EmptyState icon={<Megaphone />} title="Не удалось загрузить канал">
          {(q.error as Error | null)?.message}
        </EmptyState>
      </div>
    );
  return (
    <div className="pc ch">
      {!d.channel ? (
        <ConnectCard d={d} bot={bot} />
      ) : (
        <Card
          title={d.channel.title ?? 'Канал'}
          action={
            <Button size="sm" variant="ghost" onClick={disconnect}>
              Отключить
            </Button>
          }
        >
          <div className="ch-head">
            {d.channel.username && (
              <a href={`https://t.me/${d.channel.username}`} target="_blank" rel="noreferrer">
                @{d.channel.username}
              </a>
            )}
            <span>
              За 14 дней: <b className="tnum">{d.streak.posted}</b> постов, дней с постами{' '}
              <b className="tnum">{d.streak.days}</b> из 14
            </span>
            <span className="muted">
              {d.schedule?.enabled ? (
                <>
                  Агент заполняет слоты постов каждый день — время работы в{' '}
                  <Link to="/agent/tasks" hash="schedule">
                    «Расписании»
                  </Link>
                </>
              ) : (
                <>
                  Плановая работа выключена — включите «Пост в канал» в{' '}
                  <Link to="/agent/tasks" hash="schedule">
                    «Расписании»
                  </Link>
                </>
              )}
            </span>
          </div>
        </Card>
      )}

      {d.channel && <SlotsCard d={d} />}

      <Card title="План" count={d.upcoming.length}>
        {d.upcoming.length ? (
          <div className="ch-list">
            {d.upcoming.map((p) => (
              <PostCard key={p.id} p={p} channel={d.channel} />
            ))}
          </div>
        ) : (
          <p className="muted ch-empty">
            {d.channel
              ? 'В плане пусто. Агент заполняет слоты в плановой работе «Пост в канал» или публикует по вашей просьбе в чате.'
              : 'Подключите канал — агент будет публиковать посты каждый день по слотам.'}
          </p>
        )}
      </Card>

      {d.channel && <NewPost />}

      {d.history.length > 0 && (
        <Card title="Вышло и снято за 30 дней" count={d.history.length}>
          <div className="ch-list">
            {d.history.map((p) => (
              <PostCard key={p.id} p={p} channel={d.channel} />
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
